import { useState, useEffect } from 'react';

import { C } from '../constants';
import { subscribeAudit, isFirebaseConfigured } from '../firebase';

const ACTION_COLORS = {
  'Patient added':     '#30D158',
  'Patient deleted':   '#FF4545',
  'Status changed':    '#FF9500',
  'Vitals saved':      '#4A9EFF',
  'Lab saved':         '#BF5AF2',
  'Med added':         '#64D2FF',
  'Med DC':            '#FF6B9D',
  'Task added':        '#FFD60A',
  'Task completed':    '#30D158',
  'Issue added':       '#FF9500',
  'Issue resolved':    '#30D158',
  'Resus changed':     '#FF4545',
  'Edit':              '#5A7A94',
};

export default function AuditView({ shiftCode, pts, onClose }) {
  const [entries, setEntries] = useState([]);
  const [filterPt, setFilterPt] = useState('all');

  useEffect(() => {
    if (!shiftCode || !isFirebaseConfigured()) return;
    const unsub = subscribeAudit(shiftCode, setEntries);
    return unsub;
  }, [shiftCode]);

  const visible = filterPt === 'all'
    ? entries
    : entries.filter(e => e.patientId === filterPt);

  const fmtTime = (tsNum) => {
    if (!tsNum) return '';
    return new Date(tsNum).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
      ' ' + new Date(tsNum).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', paddingTop: 'max(16px, env(safe-area-inset-top, 16px))', paddingBottom: 12, paddingLeft: 'max(16px, env(safe-area-inset-left, 16px))', paddingRight: 'max(16px, env(safe-area-inset-right, 16px))', borderBottom: `1px solid ${C.bd}`, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4A9EFF', fontSize: 14, cursor: 'pointer', marginRight: 10, padding: '4px 8px' }}>← Back</button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.tx }}>Audit Trail</div>
          <div style={{ fontSize: 10, color: C.dim2 }}>Last 100 changes · Shift {shiftCode}</div>
        </div>
      </div>

      {/* Patient filter */}
      <div style={{ display: 'flex', gap: 6, padding: '10px 14px', overflowX: 'auto', flexShrink: 0, borderBottom: `1px solid ${C.bd}` }}>
        <button onClick={() => setFilterPt('all')}
          style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${filterPt === 'all' ? '#4A9EFF' : C.bd}`, background: filterPt === 'all' ? '#4A9EFF22' : C.bg2, color: filterPt === 'all' ? '#4A9EFF' : C.dim, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
          All
        </button>
        {pts.map(p => (
          <button key={p.id} onClick={() => setFilterPt(p.id)}
            style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${filterPt === p.id ? '#4A9EFF' : C.bd}`, background: filterPt === p.id ? '#4A9EFF22' : C.bg2, color: filterPt === p.id ? '#4A9EFF' : C.dim, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {p.name || 'Unnamed'}
          </button>
        ))}
      </div>

      {/* Log entries */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px', paddingBottom: 'max(10px, env(safe-area-inset-bottom, 10px))' }}>
        {!isFirebaseConfigured() && (
          <div style={{ textAlign: 'center', color: C.dim, fontSize: 13, padding: '40px 0' }}>
            Firebase not configured — audit log unavailable offline.
          </div>
        )}
        {isFirebaseConfigured() && visible.length === 0 && (
          <div style={{ textAlign: 'center', color: C.dim2, fontSize: 13, padding: '40px 0' }}>
            No audit entries yet
          </div>
        )}
        {visible.map((e, i) => {
          const color = ACTION_COLORS[e.action] || '#5A7A94';
          return (
            <div key={e.id || i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.bg2}`, alignItems: 'flex-start' }}>
              {/* Colour dot */}
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 5 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color }}>{e.action}</span>
                  {e.patientName && <span style={{ fontSize: 11, color: C.tx, fontWeight: 600 }}>{e.patientName}</span>}
                  <span style={{ fontSize: 10, color: C.dim2, marginLeft: 'auto' }}>{fmtTime(e.tsNum)}</span>
                </div>
                {e.detail && <div style={{ fontSize: 11, color: C.dim, marginTop: 2, wordBreak: 'break-word' }}>{e.detail}</div>}
                {e.by && <div style={{ fontSize: 10, color: '#4A9EFF88', marginTop: 1 }}>by {e.by}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
